import { useState } from "react";
import { t } from "../../lib/i18n";
import { Screen, StudyGate } from "./kit";
import { Burst, GameHeader, Intro, Result, options, rnd, shuffle } from "./games/common";

/**
 * Sifir Roket: pop the balloon with the right product. Every right answer lifts the rocket; a wrong
 * answer shows the multiplication as groups of objects so the child sees why. Missed facts come back.
 */
type Fact = { a: number; b: number };
const ROUND = 12;
const COLORS = ["#F43F5E", "#F59E0B", "#10B981", "#3B82F6"];
const THINGS = ["🍎", "⭐", "🐟", "🌸", "🍪", "⚽"];

function loadTables(): Set<number> {
  try {
    const v = JSON.parse(localStorage.getItem("studybox.sifir") ?? "[]") as number[];
    const s = new Set(v.filter((n) => n >= 1 && n <= 12));
    return s.size ? s : new Set([2, 3]);
  } catch { return new Set([2, 3]); }
}

export function SifirPage() {
  return <Screen><StudyGate><Game /></StudyGate></Screen>;
}

function Balloon({ n, color, delay, state, onTap }: { n: number; color: string; delay: number; state: "idle" | "pop" | "wrong" | "dim"; onTap: () => void }) {
  return (
    <button type="button" onClick={onTap} disabled={state !== "idle"} aria-label={String(n)}
      className={`relative mx-auto block w-20 sm:w-24 ${state === "pop" ? "animate-pop" : state === "wrong" ? "animate-shake" : "animate-bob"} ${state === "dim" ? "opacity-40" : ""}`}
      style={{ animationDelay: state === "idle" ? `${delay}s` : undefined }}>
      <svg viewBox="0 0 60 84" className="w-full drop-shadow-md">
        <path d="M30 2C14 2 4 15 4 30c0 17 14 31 26 34 12-3 26-17 26-34C56 15 46 2 30 2z" fill={color} />
        <ellipse cx="20" cy="20" rx="6" ry="10" fill="#fff" opacity=".35" />
        <path d="M27 64l3 5 3-5z" fill={color} />
        <path d="M30 69c-4 5 4 9 0 14" stroke="#94A3B8" strokeWidth="1.5" fill="none" />
        <text x="30" y="40" textAnchor="middle" fontSize="20" fontWeight="800" fill="#fff">{n}</text>
      </svg>
    </button>
  );
}

function Rocket({ level }: { level: number }) {
  return (
    <div className="relative h-56 w-14 shrink-0 overflow-hidden rounded-full bg-gradient-to-b from-indigo-900 via-indigo-700 to-sky-400 ring-4 ring-white/60" aria-hidden>
      {["12%", "30%", "48%", "66%"].map((top, i) => <span key={i} className="absolute text-[10px]" style={{ top, left: i % 2 ? "20%" : "60%" }}>✦</span>)}
      <span className="absolute left-1/2 -translate-x-1/2 text-3xl transition-all duration-500" style={{ bottom: `${4 + level * 78}%` }}>🚀</span>
      <span className="absolute left-1/2 top-1 -translate-x-1/2 text-lg">🌙</span>
    </div>
  );
}

/** a × b as a rows of b objects — shown after a wrong answer. */
function Groups({ a, b }: { a: number; b: number }) {
  const thing = THINGS[(a + b) % THINGS.length]!;
  return (
    <div className="animate-rise rounded-2xl bg-amber-50 p-3 text-center ring-1 ring-amber-200">
      <p className="mb-2 text-sm font-bold text-amber-800">{t("{a} kumpulan, setiap satu ada {b}", { a, b })}</p>
      <div className="mx-auto flex max-w-xs flex-col gap-1">
        {Array.from({ length: a }, (_, r) => (
          <div key={r} className="flex flex-wrap justify-center gap-0.5 text-base leading-none">{Array.from({ length: b }, (_, k) => <span key={k}>{thing}</span>)}</div>
        ))}
      </div>
      <p className="mt-2 text-lg font-extrabold text-amber-900">{a} × {b} = {a * b}</p>
    </div>
  );
}

function Game() {
  const [tables, setTables] = useState<Set<number>>(loadTables);
  const [deck, setDeck] = useState<Fact[] | null>(null);
  const [n, setN] = useState(0);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [opts, setOpts] = useState<number[]>([]);
  const [tapped, setTapped] = useState<number | null>(null);
  const [burst, setBurst] = useState(0);
  const [startedAt, setStartedAt] = useState(0);
  const [fast, setFast] = useState(false);

  const toggle = (k: number) => setTables((s) => {
    const next = new Set(s);
    if (next.has(k)) next.delete(k); else next.add(k);
    try { localStorage.setItem("studybox.sifir", JSON.stringify([...next])); } catch { /* ignore */ }
    return next;
  });
  const optionsFor = (f: Fact) => options(f.a * f.b, () => {
    const r = Math.random();
    return Math.max(1, r < 0.4 ? f.a * (f.b + (Math.random() < 0.5 ? 1 : -1)) : r < 0.7 ? (f.a + (Math.random() < 0.5 ? 1 : -1)) * f.b : f.a * f.b + rnd(-5, 5));
  });
  function start() {
    const facts: Fact[] = [];
    for (const a of tables) for (let b = 1; b <= 12; b++) facts.push({ a, b });
    const d = shuffle(facts).slice(0, ROUND);
    setDeck(d); setN(0); setScore(0); setLives(3); setTapped(null); setOpts(optionsFor(d[0]!)); setStartedAt(Date.now());
  }
  const fact = deck?.[n];
  const over = deck !== null && (score >= ROUND || n >= deck.length || lives <= 0);

  function tap(v: number) {
    if (!fact || tapped !== null) return;
    setTapped(v);
    if (v === fact.a * fact.b) {
      setScore((s) => s + 1); setBurst((b) => b + 1); setFast(Date.now() - startedAt < 3000);
      setTimeout(next, 700);
    } else {
      setLives((l) => l - 1); setFast(false);
      setDeck((d) => (d ? [...d, fact] : d)); // practise it again later
    }
  }
  function next() {
    setTapped(null);
    setN((i) => {
      const j = i + 1;
      if (deck && deck[j]) { setOpts(optionsFor(deck[j]!)); setStartedAt(Date.now()); }
      return j;
    });
  }

  const header = <GameHeader title={t("Sifir Roket")} emoji="🚀" tone="from-indigo-500 to-violet-500" score={deck ? score : undefined} lives={deck ? lives : undefined} progress={deck ? Math.min(1, score / ROUND) : undefined} />;

  if (!deck) {
    return (
      <>
        {header}
        <Intro emoji="🚀" title={t("Sifir Roket")} text={t("Pecahkan belon dengan jawapan yang betul untuk terbangkan roket ke bulan!")} onStart={start}>
          <p className="text-sm font-bold text-slate-700">{t("Pilih sifir")}</p>
          <div className="grid grid-cols-6 gap-2">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((k) => (
              <button key={k} type="button" aria-pressed={tables.has(k)} onClick={() => toggle(k)}
                className={`rounded-xl py-2 text-sm font-extrabold ${tables.has(k) ? "bg-indigo-500 text-white" : "bg-slate-100 text-slate-500"}`}>{k}</button>
            ))}
          </div>
        </Intro>
      </>
    );
  }
  if (over) return <>{header}<Result game="sifir" score={score} max={ROUND} onAgain={start} details={{ tables: [...tables].join(",") }} /></>;

  const right = fact!.a * fact!.b;
  const wrongTap = tapped !== null && tapped !== right;
  return (
    <>
      {header}
      <Burst show={burst} />
      <div className="flex gap-4">
        <Rocket level={Math.min(1, score / ROUND)} />
        <div className="flex-1 space-y-4">
          <div key={n} className="animate-rise rounded-3xl bg-card p-5 text-center shadow-sm ring-1 ring-black/5">
            <p className="text-5xl font-black tracking-tight text-slate-900">{fact!.a} × {fact!.b}</p>
            <p className="mt-1 h-5 text-sm font-bold text-emerald-600">{tapped === right ? (fast ? t("Betul! Laju!") : t("Betul!")) : ""}</p>
          </div>
          <div className="grid grid-cols-2 gap-y-2">
            {opts.map((v, i) => (
              <Balloon key={`${n}-${v}`} n={v} color={COLORS[i % 4]!} delay={i * 0.3} onTap={() => tap(v)}
                state={tapped === null ? "idle" : v === tapped ? (v === right ? "pop" : "wrong") : v === right && wrongTap ? "idle" : "dim"} />
            ))}
          </div>
        </div>
      </div>
      {wrongTap && (
        <div className="mt-4 space-y-3">
          <Groups a={fact!.a} b={fact!.b} />
          <button type="button" onClick={next} className="w-full rounded-full bg-violet-500 py-3 text-base font-extrabold text-white shadow-md">{t("Faham! Seterusnya →")}</button>
        </div>
      )}
    </>
  );
}
